<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          We are required to gather this information for any account holder,
          based on The Patriot Act
        </p>
      </div>
    </help-drawer>
    <v-form name="CompanyAddress" class="form" @submit.prevent data-test-id="company-address-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="company-address-header">
              <span class="headerlight">
                Tell us a bit about your <strong>company.</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your information...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isCompanyAddressComplete && formHidden && !isSyncing"
          :submitted="isCompanyAddressComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container large">
              <v-row class="mobile">
                <v-col class="full">
                  <label for="searchCompanyAddress" class="required" data-test-id="search-company-label">
                    Search Company/Address
                  </label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="searchCompanyAddress"
                    id="searchCompanyAddress"
                    aria-required="true"
                    v-model.trim="searchCompanyAddress"
                    :backgroundColor="inputBackgroundColor"
                    placeHolder="Search"
                    @input="$v.searchCompanyAddress.$touch()"
                    @blur="$v.searchCompanyAddress.$touch()"
                    :error-messages="searchCompanyAddressErrors"
                    hide-details="auto"
                    class="mb-2"
                    data-test-id="search-company-input"
                  />
                </v-col>
              </v-row>
              <v-row v-if="company.autoFilled">
                <v-col class="full">
                  <label for="companyName" class="required" data-test-id="company-name-label">Company Name</label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="companyName"
                    aria-required="true"
                    v-model.trim="company.name"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.company.name.$touch()"
                    @blur="$v.company.name.$touch()"
                    :error-messages="companyNameErrors"
                    hide-details="auto"
                    class="mb-2"
                    data-test-id="company-name-input"
                  />
                </v-col>
              </v-row>
              <v-row class="mobile" v-if="company.autoFilled">
                <v-col>
                  <label for="address1" class="required" data-test-id="address1-label">Address 1</label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="address1"
                    aria-required="true"
                    v-model.trim="company.address1"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.company.address1.$touch()"
                    @blur="$v.company.address1.$touch()"
                    :error-messages="companyAddress1Errors"
                    hide-details="auto"
                    class="mb-2"
                    data-test-id="address1-input"
                  />
                </v-col>
                <v-col>
                  <label for="address2" data-test-id="address2-label">Address 2</label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="address2"
                    v-model.trim="company.address2"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.company.address2.$touch()"
                    @blur="$v.company.address2.$touch()"
                    hide-details="auto"
                    class="mb-2"
                    data-test-id="address2-input"
                  />
                </v-col>
              </v-row>
              <v-row class="mobile" v-if="company.autoFilled">
                <v-col class="col-6">
                  <label for="city" class="required" data-test-id="city-label">City</label>
                  <v-text-field
                    outlined
                    type="text"
                    maxlength="256"
                    name="city"
                    aria-required="true"
                    v-model.trim="company.city"
                    :backgroundColor="inputBackgroundColor"
                    @input="$v.company.city.$touch()"
                    @blur="$v.company.city.$touch()"
                    :error-messages="companyCityErrors"
                    hide-details="auto"
                    data-test-id="city-input"
                  />
                </v-col>
                <v-col>
                  <v-row class="state-zip">
                    <v-col>
                      <label for="state" class="required" data-test-id="state-label">State</label>
                      <v-select
                        v-model="company.state"
                        ref="state"
                        :items="states"
                        :backgroundColor="inputBackgroundColor"
                        outlined
                        placeholder="Choose"
                        dense
                        hide-details="auto"
                        @change="$v.company.state.$touch()"
                        :error-messages="companyStateErrors"
                        data-test-id="state-select"
                      />
                    </v-col>
                    <v-col>
                      <label for="zip" class="required" data-test-id="zip-label">Zip Code</label>
                      <v-text-field
                        outlined
                        type="text"
                        maxlength="10"
                        name="zip"
                        aria-required="true"
                        v-model.trim="company.zip"
                        :backgroundColor="inputBackgroundColor"
                        @input="$v.company.zip.$touch()"
                        @blur="$v.company.zip.$touch()"
                        :error-messages="companyZipErrors"
                        hide-details="auto"
                        inputmode="numeric"
                        data-test-id="zip-input"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="company-address-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import BottomNav from '@/components/common/BottomNav';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS, validateZip, STATES } from '@/common/constants';
import { required } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

/* Custom validator */
const mustSelectResult = (value, vm) => {
  return value !== null && vm.company.autoFilled;
};

export default {
  name: 'PershingCompanyAddress',

  components: { BottomNav, DataSubmittedMessage, HelpDrawer, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    MENUS,
    STATES: STATES,
    searchCompanyAddress: null,
    company: {
      autoFilled: false,
      name: null,
      address1: null,
      address2: null,
      city: null,
      state: null,
      zip: null,
    },
    formHidden: true,
  }),
  validations() {
    return {
      searchCompanyAddress: {
        //required,
        mustSelectResult,
      },
      company: {
        name: {
          required,
        },
        address1: {
          required,
        },
        address2: {},
        city: {
          required,
        },
        state: {
          required,
        },
        zip: {
          required,
          validateZip,
        },
      },
      validationGroup: [
        'searchCompanyAddress',
        'company.name',
        'company.address1',
        'company.address2',
        'company.city',
        'company.state',
        'company.zip',
      ],
    };
  },
  computed: {
    // Dynamic Search errors */
    searchCompanyAddressErrors() {
      const errors = [];
      if (!this.$v.searchCompanyAddress.$dirty) return errors;
      //!this.$v.searchCompanyAddress.required && errors.push("You must search/select a Company/Address");
      !this.$v.searchCompanyAddress.mustSelectResult &&
        errors.push('You must search/select a Company/Address');
      return errors;
    },

    // Dynamic Name errors */
    companyNameErrors() {
      const errors = [];
      if (!this.$v.company.name.$dirty) return errors;
      !this.$v.company.name.required && errors.push('Company Name is required');
      return errors;
    },

    // Dynamic Address1 errors */
    companyAddress1Errors() {
      const errors = [];
      if (!this.$v.company.address1.$dirty) return errors;
      !this.$v.company.address1.required &&
        errors.push('Address 1 is required');
      return errors;
    },

    // Dynamic City errors */
    companyCityErrors() {
      const errors = [];
      if (!this.$v.company.city.$dirty) return errors;
      !this.$v.company.city.required && errors.push('City is required');
      return errors;
    },

    // Dynamic State errors */
    companyStateErrors() {
      const errors = [];
      if (!this.$v.company.state.$dirty) return errors;
      !this.$v.company.state.required && errors.push('State is required');
      return errors;
    },

    // Dynamic Zip errors */
    companyZipErrors() {
      const errors = [];
      if (!this.$v.company.zip.$dirty) return errors;
      !this.$v.company.zip.required && errors.push('Zip Code is required');
      !this.$v.company.zip.validateZip &&
        errors.push('Zip Code must be NNNNN or NNNNN-NNNN');
      return errors;
    },
    helpTitle() {
      return 'Why do you need this?';
    },
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id);
      return page && page.status === 'IN_PROGRESS';
    },
    // Is company address complete
    isCompanyAddressComplete() {
      return this.isPageComplete(MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id);
    },
  },
  methods: {
    /* 
      Override toggle form state from hidden/shown.
      If hidden, reset form errors, else init autocomplete
    */
    toggleFormState(hide) {
      this.formHidden = hide;
      if (hide) {
        this.$v.$reset();
      } else {
        this.$nextTick(() => this.initAutoComplete());
      }
    },

    /* Nav to Next Page in flow */
    next(route = null) {
      let to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        name: this.company.name,
        address: {
          line1: this.company.address1,
          line2: this.company.address2,
          city: this.company.city,
          state: this.company.state,
          zip: this.company.zip,
        },
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(this.pages[this.pageIndex]);

      this.completeStep(this.pages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },

    /* Fill in Address Fields from Autocomplete */
    fillInAddress() {
      // Get the place details from the autocomplete object.
      const place = this.autocomplete.getPlace();
      const addressObj = this.company;
      let addressField = document.querySelector('#searchCompanyAddress');
      let address1 = '';
      let postcode = '';
      addressObj.autoFilled = true;

      // Get each component of the address from the place details,
      // and then fill-in the corresponding field on the form.
      // place.address_components are google.maps.GeocoderAddressComponent objects
      // which are documented at http://goo.gle/3l5i5Mr
      for (const component of place.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case 'street_number': {
            address1 = `${component.long_name} ${address1}`;
            break;
          }

          case 'route': {
            address1 += component.short_name;
            break;
          }

          case 'postal_code': {
            postcode = `${component.long_name}${postcode}`;
            break;
          }

          case 'postal_code_suffix': {
            postcode = `${postcode}-${component.long_name}`;
            break;
          }
          case 'locality': {
            addressObj.city = component.long_name;
            break;
          }
          case 'administrative_area_level_1': {
            addressObj.state = component.short_name;
            break;
          }
        }
      }
      this.$nextTick(() => (addressField.value = place.formatted_address));
      addressObj.name = place.name;
      addressObj.address1 = address1;
      addressObj.zip = postcode;

      // Clone Company for read-only detection
      this.origCompanyAddress = { ...this.company };
    },

    /* Initialize the Autocomplete widget */
    initAutoComplete() {
      let addressField = document.querySelector('#searchCompanyAddress');

      this.autocomplete = new window.google.maps.places.Autocomplete(
        addressField,
        {
          componentRestrictions: { country: ['us'] },
          fields: ['address_components', 'name', 'formatted_address'],
          types: [],
        },
      );

      // When the user selects an address from the drop-down, populate the
      // address fields in the form.
      this.autocomplete.addListener('place_changed', this.fillInAddress);
    },

    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isCompanyAddressComplete) {
        this.formHidden = true;
      }
    }
  },
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isCompanyAddressComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  mounted() {
    if (!this.formHidden) this.initAutoComplete();
    // Check form state on mount
    this.$nextTick(() => {
      // If company address is complete, hide the form
      if (this.isCompanyAddressComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  },

  created() {
    this.interviewPages.addEmploymentPages();
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id);
    // Initialize form data for current interview
    this.states = STATES;
    this.formHidden = this.isCompanyAddressComplete;
  },
};
</script>
<style lang="scss" scoped>
.state-zip {
  @media screen and (max-width: 960px) {
    padding-top: 8px !important;
  }
}
.alternate-address {
  margin-top: 30px;
  border-top: 1px solid var(--secondary-color);
}
.cb-label {
  margin-bottom: -5px;
}
.col.full {
  max-width: 100% !important;
}
.col.instructions {
  @media screen and (max-width: 960px) {
    strong {
      display: block;
    }
  }
}
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
